import { NodeProp } from '@lezer/common';
import { parser } from '@plutojl/lezer-julia';
import { LRLanguage, indentNodeProp, continuedIndent, LanguageSupport } from '@codemirror/language';
import { styleTags, tags } from '@lezer/highlight';
import * as autocomplete from '@codemirror/autocomplete';

function delimitedStrategy(context, units, closing) {
    let after = context.textAfter;
    let space = after.match(/^\s*/)[0].length;
    let closed = false;
    switch (closing.length) {
        case 1:
            closed = after.slice(space, space + closing[0].length) === closing[0];
            break;
        case 2:
            closed =
                after.slice(space, space + closing[0].length) === closing[0] ||
                    after.slice(space, space + closing[1].length) === closing[1];
            break;
        case 3:
            closed =
                after.slice(space, space + closing[0].length) === closing[0] ||
                    after.slice(space, space + closing[1].length) === closing[1] ||
                    after.slice(space, space + closing[2].length) === closing[2];
            break;
        default:
            closed = closing.some((closing) => after.slice(space, space + closing.length) === closing);
            break;
    }
    return context.baseIndent + (closed ? 0 : context.unit * units);
}
function delimitedIndent({ closing, units = 1, }) {
    return (context) => delimitedStrategy(context, units, closing);
}
function noIndent(context) {
    return context.baseIndent;
}

function getSyntaxConfig() {
    let syntaxConfig = {
        indents: {
            VariableDeclaration: continuedIndent(),
            AssignmentExpression: continuedIndent(),
        },
        keywords: [],
    };
    for (let node of parser.nodeSet.types) {
        // Collect keywords
        let groups = node.prop(NodeProp.group);
        let group = groups != null ? groups[0] : null;
        if (group === "keyword") {
            syntaxConfig.keywords.push(node);
        }
        // Configure indents
        let nodeIndent;
        let closedBy = node.prop(NodeProp.closedBy);
        if (closedBy) {
            nodeIndent = delimitedIndent({ closing: closedBy });
        }
        else {
            nodeIndent = noIndent;
        }
        syntaxConfig.indents[node.name] = nodeIndent;
    }
    return syntaxConfig;
}
let syntaxConfig = /*@__PURE__*/getSyntaxConfig();
let juliaStyleTags = /*@__PURE__*/styleTags({
    String: tags.string,
    TripleString: tags.string,
    CommandString: tags.string,
    StringWithoutInterpolation: tags.string,
    TripleStringWithoutInterpolation: tags.string,
    CommandStringWithoutInterpolation: tags.string,
    "String/$ TripleString/$ CommandString/$": /*@__PURE__*/tags.special(tags.brace),
    "String/( TripleString/( CommandString/(": /*@__PURE__*/tags.special(tags.brace),
    "String/) TripleString/) CommandString/)": /*@__PURE__*/tags.special(tags.brace),
    Comment: tags.lineComment,
    BlockComment: tags.comment,
    "mutable struct StructDefinition/end": tags.definitionKeyword,
    "primitive type PrimitiveDefinition/end": tags.definitionKeyword,
    "const local global": tags.definitionKeyword,
    // "module ModuleDefinition/end import using export": t.moduleKeyword,
    "ForStatement/for ForBinding/in ForStatement/end": tags.controlKeyword,
    "WhileStatement/while WhileStatement/end": tags.controlKeyword,
    "IfClause/if IfClause/elseif ElseClause/else IfStatement/end": tags.controlKeyword,
    "default break return": tags.controlKeyword,
    "TryStatement/try CatchClause/catch TryElseClause/else FinallyClause/finally TryStatement/end": tags.controlKeyword,
    "( )": tags.paren,
    "[ ]": tags.paren,
    "{ }": tags.paren,
    [/*@__PURE__*/syntaxConfig.keywords.map((t) => t.name).join(" ")]: tags.keyword,
    BooleanLiteral: tags.bool,
    Number: tags.number,
    "Coefficient/PrefixedString!": tags.unit,
    // Look at us being rascals
    "Type! TypeParameters!": tags.typeName,
    // "StructDefinition/Definition! PrimitiveDefinition/Definition! AbstractDefinition/Definition!":
    //   t.definition(t.typeName),
    "StructDefinition/Identifier StructDefinition/AssignmentExpression/Identifier StructDefinition/TypedExpression/Identifier StructDefinition/AssignmentExpression/TypedExpression/Identifier": /*@__PURE__*/tags.definition(tags.propertyName),
    ":: <:": tags.typeOperator,
    Identifier: tags.variableName,
    "MacroIdentifier! MacroFieldExpression!": tags.macroName,
    "MacroDefinition/Definition!": /*@__PURE__*/tags.definition(tags.macroName),
    "FieldName!": tags.propertyName,
    FieldExpression: tags.propertyName,
    "FieldExpression .": tags.derefOperator,
    "Symbol!": tags.atom,
});
let language = /*@__PURE__*/LRLanguage.define({
    parser: /*@__PURE__*/parser.configure({
        props: [
            juliaStyleTags,
            /*@__PURE__*/indentNodeProp.add(/*@__PURE__*/Object.assign(/*@__PURE__*/Object.assign({}, syntaxConfig.indents), { ModuleDefinition: noIndent, BareModuleDefinition: noIndent, VariableDeclaration: /*@__PURE__*/continuedIndent(), AssignmentExpression: /*@__PURE__*/continuedIndent() })),
        ],
    }),
    languageData: {
        commentTokens: { line: "#" },
        indentOnInput: /^\s*(\]|\}|\)|end|else|elseif|catch|finally)$/,
        closeBrackets: { brackets: ["(", "[", "{", "'", '"', "`"] },
    },
});
const keywordCompletion = /*@__PURE__*/language.data.of({
    autocomplete: /*@__PURE__*/autocomplete.completeFromList(/*@__PURE__*/syntaxConfig.keywords.map((keyword) => ({
        label: keyword.name,
        type: "keyword",
    }))),
});
let defaultConfig = {
    enableKeywordCompletion: false,
};
function julia(config = defaultConfig) {
    config = Object.assign(Object.assign({}, defaultConfig), config);
    let extensions = [];
    if (config.enableKeywordCompletion) {
        extensions.push(keywordCompletion);
    }
    return new LanguageSupport(language, extensions);
}

export { julia, keywordCompletion };
